/// <amd-module name="Build/Pipelines/PipelineDetailsView" />
import {
    BuildDefinition,
    DefinitionQuality,
    DefinitionQueueStatus,
    DefinitionReference,
    DefinitionType,
    YamlBuild,
    YamlProcess
} from "Build/Client/Contracts/Build";
import { GeneralSettingsData, PipelineDetailsData, PipelineServiceConnection } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { ISaveDefinitionProps } from "Build/Common/Commands/SaveDefinition/Command.types";
import { CommandComponent, prefetch } from "Build/Common/Library/Components/CommandComponent";
import { UpdateBitbucketServiceConnectionHeader } from "Build/Common/Library/Components/UpdateBitBucketServiceConnectionHeader";
import { UpgradeServiceConnectionHeader } from "Build/Common/Library/Components/UpgradeServiceConnectionHeader";
import { BuildOptionGUIDs, ProcessType } from "Build/Common/Library/Constants";
import { DefinitionCallbackType, IDefinitionDetailContributionContext } from "Build/Common/Library/ContributedExtension.types";
import { handleError, registerErrorHandler } from "Build/Common/Library/ErrorHandler";
import { PipelineSettings, PipelineSettingsPanel } from "Build/Common/Library/Legacy/Components/PipelineSettingsPanel";
import { ItemOrdering, ObservableHeaderCommandArray } from "Build/Common/Library/Legacy/ObservablePivotActionArray";
import { getPivotItemToTabProvider } from "Build/Common/Library/Legacy/PivotItemToTabProvider";
import * as LibraryResources from "Build/Common/Library/Resources";
import { BuildRequiredPermissions, Security } from "Build/Common/Library/Security";
import { clearTreeStateOnUnmount } from "Build/Common/Library/StateHandler";
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import { BuildSignalRActionCreator, BuildSignalRActionHub } from "Build/Pipelines/Actions/SignalR";
import { PipelineTriggerIssuesPage } from "Build/Pipelines/Components/PipelineTriggerIssuesPage";
import { ScheduledRunsPanel } from "Build/Pipelines/Components/ScheduledRunsPanel";
import { StatusBadgePanel } from "Build/Pipelines/Components/StatusBadgePanel";
import { Performance, disposeFPS } from "Build/Pipelines/Performance";
import * as PipelineActions from "Build/Pipelines/PipelineActions";
import { IPipelineDetailsDataService } from "Build/Pipelines/PipelineDetailsDataService";
import { PipelinesHeaderBreadcrumbService } from "Build/Pipelines/PipelinesHeaderBreadcrumbService";
import * as Resources from "Build/Pipelines/Resources";
import { BuildSignalRHub } from "Build/Pipelines/SignalRHub";
import { StatusBadgePanelStore } from "Build/Pipelines/Stores/StatusBadgePanelStore";
import { BranchesView } from "Build/Pipelines/Tabs/Branches/BranchesView";
import { IGeneralSettingsService } from "Build/Settings/general/GeneralSettingsService";
import * as PipelineTriggerIssuesStore from "Stores/PipelineTriggerIssuesStore";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { RunsFilterBar } from "Tabs/Runs/RunsFilterBar";
import { RunsTable } from "Tabs/Runs/RunsTable";
import { RunsAppliedFilterStore } from "Tabs/Runs/Stores/RunsAppliedFilterStore";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableArray, IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints, ScreenSize } from "VSS/Core/Util/Screen";
import { equals, format } from "VSS/Core/Util/String";
import { ContributedPillGroup, getPills } from "VSS/Features/PlatformUI/ContributedPillGroup";
import { onClickFPS } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IButtonProps } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Header, IHeaderBreakpoint, TitleSize } from "VSSUI/Header";
import { HeaderCommandBarWithFilter, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { IObservableViewStateUrl } from "VSSUI/Navigation/ViewStateNavigation";
import { IVssHubViewState, VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { Page } from "VSSUI/Page";
import { PillSize } from "VSSUI/Pill";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, Tab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { Filter } from "VSSUI/Utilities/Filter";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import * as React from "react";
import { INewBranchFilter } from "./Tabs/Runs/RunsFilterBar.types";

export interface IPipelineDetailsViewContentProps extends IVssComponentProperties {}

export interface IPipelineDetailsViewContentState {
    selectedTab: string;
    pipelineDetails: PipelineDetailsData | string | undefined;
    settingsPanelIsOpen: boolean;
    settingsErrorMessage: string;
    settingsIsSaving: boolean;
    scheduledRunsPanelIsOpen: boolean;
    triggerIssuesLength: number;
    triggerIssuesData: PipelineTriggerIssuesStore.ITriggerIssuesGridRow[];
    newBranches: INewBranchFilter[];
    isClassicPipelineCreationDisabled: boolean;
}

namespace Tabs {
    export const Branches = "branches";
    export const Runs = "runs";
    export const TriggerIssues = "triggerIssues";
}

class PipelineDetailsViewContent extends VssComponent<IPipelineDetailsViewContentProps, IPipelineDetailsViewContentState> {
    private _dataService: IPipelineDetailsDataService;
    private _projectSettingsService: IGeneralSettingsService;
    private _isPageLoad: boolean = true;
    private _permissions: PermissionsStore;
    private _security: Security;
    private _statusBadgePanelStore: StatusBadgePanelStore;
    private _runsFilter: Filter;
    private _runsFilterToggled: ObservableValue<boolean>;
    private _runsAppliedFilterStore: RunsAppliedFilterStore;
    private _hubViewState: IVssHubViewState;
    private _viewUrls: { [key: string]: IObservableViewStateUrl } = {};
    private _extensionDefinitionCallBacks: { [key: string]: DefinitionCallbackType } = {};
    private _buildDefinition: BuildDefinition = {} as BuildDefinition;

    private _signalRActionHub: BuildSignalRActionHub;
    private _signalRHub: BuildSignalRHub;

    private _headerCommands: ObservableHeaderCommandArray;
    private _triggerIssuesStore: PipelineTriggerIssuesStore.PipelineTriggerIssuesStore;
    private _triggerIssuesState: PipelineTriggerIssuesStore.ITriggerIssuesState;
    private _selectedTabIdBackUp: string;

    private buttonProps: IButtonProps[] = [
        {
            text: Resources.ViewDetails,
            onClick: () => {
                this._selectedTabIdBackUp = this.state.selectedTab;
                this._hubViewState.selectedPivot.value = Tabs.TriggerIssues;
                this.setState({ selectedTab: Tabs.TriggerIssues });
            }
        }
    ];

    constructor(props: IPipelineDetailsViewContentProps, context: IVssComponentContext) {
        super(props, context);

        this._dataService = this.context.pageContext.getService<IPipelineDetailsDataService>("IPipelineDetailsDataService");
        this._projectSettingsService = this.context.pageContext.getService<IGeneralSettingsService>("IGeneralSettingsService");
        this._hubViewState = new VssHubViewState(context.pageContext, { defaultPivot: Tabs.Runs });

        const tfsPageData = getTFSPageData(this.context.pageContext);
        const vssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        this._signalRActionHub = new BuildSignalRActionHub();
        if (tfsPageData && tfsPageData.project && vssPageData) {
            this._signalRHub =
                this.props.signalRHub ||
                new BuildSignalRHub({
                    actionCreator: new BuildSignalRActionCreator({ actionHub: this._signalRActionHub }),
                    hostInformation: {
                        hostId: vssPageData.hostId,
                        isHosted: vssPageData.isHosted,
                        hostPath: vssPageData.hostPath,
                        projectId: tfsPageData.project.id
                    },
                    pageContext: this.context.pageContext,
                    projectId: tfsPageData.project.id
                });
        }

        this._permissions = new PermissionsStore({
            pageContext: context.pageContext
        });

        this._security = new Security({
            projectId: tfsPageData && tfsPageData.project ? tfsPageData.project.id : "",
            store: this._permissions
        });

        this._statusBadgePanelStore = new StatusBadgePanelStore({
            pageContext: context.pageContext
        });

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();

        // the PageStateHistoryService will add our filter state into the URL, with each key being named something like "statusFilter"
        // if any of these keys exist, we should show the filterbar so it can be initialized with this state
        const filterBarInitiallyVisible = Object.keys(historyState).some(key => key.endsWith("Filter"));

        this._runsFilter = new Filter();
        this._runsFilterToggled = new ObservableValue<boolean>(filterBarInitiallyVisible);

        this._runsAppliedFilterStore = new RunsAppliedFilterStore(this._runsFilter);

        // trigger issues
        this._triggerIssuesStore = new PipelineTriggerIssuesStore.PipelineTriggerIssuesStore(
            context.pageContext,
            this._dataService.data ? this._dataService.data.value.definitionId : -1
        );
        this._triggerIssuesState = this._triggerIssuesStore.getState();

        this.state = {
            selectedTab: this._hubViewState.selectedPivot.value,
            pipelineDetails: this._dataService.data ? this._dataService.data.value : undefined,
            settingsPanelIsOpen: false,
            settingsErrorMessage: "",
            settingsIsSaving: false,
            scheduledRunsPanelIsOpen: false,
            triggerIssuesLength: 0,
            triggerIssuesData: this._triggerIssuesState.triggerIssueData,
            newBranches: [],
            isClassicPipelineCreationDisabled: this._projectSettingsService.data?.value.disableClassicBuildPipelineCreation ? 
                this._projectSettingsService.data.value.disableClassicBuildPipelineCreation.enabled : true
        };
    }

    public componentWillMount() {
        this._triggerIssuesStore.addListener(this._onStoreChanged);
    }

    public render() {
        if (this.state.pipelineDetails === undefined) {
            return (
                <Surface background={SurfaceBackground.neutral}>
                    <Page className="pipelines-page">
                        <MessageCard severity={MessageCardSeverity.Error}>{Resources.PipelineNotFound}</MessageCard>
                    </Page>
                </Surface>
            );
        }
        if (typeof this.state.pipelineDetails === 'string') {
            return (
                <Surface background={SurfaceBackground.neutral}>
                    <Page className="pipelines-page">
                        <MessageCard severity={MessageCardSeverity.Error}>{this.state.pipelineDetails}</MessageCard>
                    </Page>
                </Surface>
            );
        }

        const isYamlProcess: boolean = this.state.pipelineDetails.definitionProcessType === ProcessType.Yaml;
        const canQueueBuild: boolean = this._security.canQueueBuild({
            id: this.state.pipelineDetails.definitionId,
            path: this.state.pipelineDetails.definitionPath
        } as DefinitionReference);

        const pageTitle = this.state.pipelineDetails.definitionName || Resources.PipelineDetailsTitle;

        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        const userId = vssPageData ? vssPageData.user.id : undefined;

        const pipelineData = this._dataService.data.value;

        let definition = {} as BuildDefinition;
        let connection = {} as PipelineServiceConnection;

        if (pipelineData) {
            definition = pipelineData.definition || {};
            connection = pipelineData.serviceConnection || {};
        }

        return (
            <Surface background={SurfaceBackground.neutral}>
                {this.state.selectedTab !== Tabs.TriggerIssues && (
                <Page className="pipelines-page" selectedTabId={this._hubViewState.selectedPivot} tabProviders={this._getTabProviders()}>
                        <Header
                            title={
                                <>
                                    <span>{pageTitle}</span>
                                    <ContributedPillGroup
                                        className="margin-left-8 flex-noshrink"
                                        pageContext={this.context.pageContext}
                                        pills={getPills(
                                            this.context.pageContext,
                                            "ms.vss-build-web.build-definition-detail-view-header-pills",
                                            this.state.pipelineDetails
                                        )}
                                        size={PillSize.compact}
                                    />
                                </>
                            }
                            titleClassName="flex-row flex-center"
                            description={this.state.pipelineDetails.isDraft ? Resources.DraftDescription : undefined}
                            titleSize={TitleSize.Large}
                            commandBarItems={this._getCommandBarItems()}
                            headerBreakpoints={this._getCommandBarBreakpoints()}
                            backButtonProps={{ onClick: this._onClickHeaderBack, text: Resources.PipelinesHeader }}
                            className="ci-pipeline-details-header"
                        />
                        {isFeatureFlagEnabled(this.context.pageContext, "Build2.BuildSummaryPage.SwitchToAppConnection", false) && (
                            <UpgradeServiceConnectionHeader className="padding-top-16" definition={definition} connection={connection} />
                        )}
                        {isFeatureFlagEnabled(this.context.pageContext, "Build2.BitbucketAzurePipelinesBackupOAuthClient", false) && connection.isOutdated && (
                            <UpdateBitbucketServiceConnectionHeader className="padding-top-16" definition={definition} connection={connection} />
                        )}
                        <TabBar tabSize={TabSize.Tall} onSelectedTabChanged={this._onTabChanged} renderAdditionalContent={this._getViewCommandBar}>
                            <Tab name={Resources.RunsTabName} id={Tabs.Runs} key={Tabs.Runs} />
                            <Tab name={Resources.BranchesTabName} id={Tabs.Branches} key={Tabs.Branches} />
                        </TabBar>
                        {this.state.selectedTab === Tabs.Runs && (
                            <ConditionalChildren renderChildren={this._runsFilterToggled}>
                                <RunsFilterBar
                                    appliedFilterStore={this._runsAppliedFilterStore}
                                    definitionId={this.state.pipelineDetails.definitionId}
                                    newBranches={this.state.newBranches}
                                />
                            </ConditionalChildren>
                        )}
                        <div className="page-content page-content-top">
                            {this.state.pipelineDetails.throttleMessage && (
                                <MessageCard
                                    severity={MessageCardSeverity.Warning}
                                    className="margin-bottom-16"
                                    buttonProps={this._getThrottleButton(this.state.pipelineDetails.incidentId)}
                                >
                                    {this.state.pipelineDetails.throttleMessage}
                                </MessageCard>
                            )}
                            {isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.EnableYamlPipelineTriggerIssues", false) &&
                                this.state.triggerIssuesLength > 0 && (
                                    <MessageCard
                                        buttonProps={this.buttonProps}
                                        className="flex-self-stretch margin-bottom-16"
                                        severity={MessageCardSeverity.Error}
                                    >
                                        {Resources.TriggerIssuesCardMessage}
                                    </MessageCard>
                                )}
                            <TabContent>
                                {this.state.selectedTab === Tabs.Runs && (
                                    <RunsTable
                                        isPageLoad={this._isPageLoad}
                                        definitionId={this.state.pipelineDetails.definitionId}
                                        canQueueBuild={canQueueBuild}
                                        appliedFilterStore={this._runsAppliedFilterStore}
                                        signalRActionHub={this._signalRActionHub}
                                        security={this._security}
                                        userId={userId}
                                        getBranchesOnNewRun={this._getNewBranches}
                                    />
                                )}
                                {this.state.selectedTab === Tabs.Branches && (
                                    <BranchesView isPageLoad={this._isPageLoad} definitionId={this.state.pipelineDetails.definitionId} />
                                )}
                            </TabContent>
                        </div>
                        <StatusBadgePanel statusBadgePanelStore={this._statusBadgePanelStore} />
                        {isYamlProcess && this.state.settingsPanelIsOpen && (
                            <PipelineSettingsPanel
                                key={this.state.pipelineDetails.definitionId}
                                definition={this._buildDefinition}
                                branch={
                                    this._buildDefinition && this._buildDefinition.repository ? this._buildDefinition.repository.defaultBranch : ""
                                }
                                isYamlDirty={false}
                                errorMessage={this.state.settingsErrorMessage}
                                isOpen={this.state.settingsPanelIsOpen}
                                isSaving={this.state.settingsIsSaving}
                                onSave={this._onSavePipelineSettings}
                                onDismiss={this._dismissPipelineSettingsPanel}
                                onError={message => {
                                    this.setState({ settingsErrorMessage: message });
                                }}
                            />
                        )}
                        {this.state.scheduledRunsPanelIsOpen && (
                            <ScheduledRunsPanel
                                key={this.state.pipelineDetails.definitionId + 1}
                                isOpen={this.state.scheduledRunsPanelIsOpen}
                                onDismiss={this._dismissScheduledRunsPanel}
                                definitionId={this.state.pipelineDetails.definitionId}
                            />
                        )}
                    </Page>
                )}
                {this.state.selectedTab === Tabs.TriggerIssues && (
                    <PipelineTriggerIssuesPage
                        definitionId={this.state.pipelineDetails.definitionId}
                        onBackButton={this.onTriggerIssuesBackButton}
                        triggerIssuesData={this.state.triggerIssuesData}
                    />
                )}
            </Surface>
        );
    }

    public componentDidMount() {
        prefetch(this.context.pageContext);
        this._signalRHub.watchProject();
        this._hubViewState.selectedPivot.subscribe(this._onViewStateChanged);
        if (this._dataService.data) {
            this._dataService.data.subscribe(this._pipelineDetailsChanged);
        }

        if (this._projectSettingsService.data){
            this._projectSettingsService.data.subscribe(this._projectSettingsChanged);
        }

        registerErrorHandler((error: Error) => {
            if (error) {
                const globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
                globalMessageService.addBanner({
                    message: error.message,
                    level: GlobalMessageLevel.error
                });
            }
        });

        if (this.state.pipelineDetails && !(typeof this.state.pipelineDetails === 'string')) {
            const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
            const definitionUrl = linkingService.getDefinitionUrl(this.state.pipelineDetails.definitionId);

            const breadcrumbService = this.context.pageContext.getService<PipelinesHeaderBreadcrumbService>("pipelines-header-breadcrumb-service");
            breadcrumbService.setPipeline(this.state.pipelineDetails.definitionName, definitionUrl, this.state.pipelineDetails.definitionPath);
            this._setTitle(this.state.selectedTab);
        }
    }

    public componentWillUnmount() {
        this._signalRHub.dispose();
        this._hubViewState.selectedPivot.unsubscribe(this._onViewStateChanged);
        if (this._dataService.data) {
            this._dataService.data.unsubscribe(this._pipelineDetailsChanged);
        }

        if (this._projectSettingsService.data) {
            this._projectSettingsService.data.unsubscribe(this._projectSettingsChanged);
        }

        this._hubViewState.dispose();

        const breadcrumbService = this.context.pageContext.getService<PipelinesHeaderBreadcrumbService>("pipelines-header-breadcrumb-service");
        breadcrumbService.clearPipeline();

        registerErrorHandler((error: Error) => {});
        Object.keys(this._viewUrls).forEach(key => {
            this._viewUrls[key].dispose();
        });

        disposeFPS();

        clearTreeStateOnUnmount(this.context.pageContext);
    }

    private _getThrottleButton(incidentId: string): IButtonProps[] | undefined {
        let throttleButton: IButtonProps[] | undefined = undefined;
        if (incidentId) {
            throttleButton = [
                {
                    text: Resources.LearnMore,
                    onClick: (() => window.open("https://portal.microsofticm.com/imp/v3/incidents/details/" + incidentId))
                }
            ];
        }
        return throttleButton;
    }

    private _onStoreChanged = () => {
        const state = this._triggerIssuesStore.getState();
        const _triggerIssuesLength = this._getTriggerIssuesLength(this._triggerIssuesState.triggerIssueData);
        this.setState({ triggerIssuesLength: _triggerIssuesLength, triggerIssuesData: state.triggerIssueData });
    };

    private _getNewBranches = (branches: INewBranchFilter[]) => {
        this.setState({
            newBranches: branches
        });
    };

    private _getViewCommandBar = () => {
        if (this.state.selectedTab === Tabs.Runs) {
            return (
                <ScreenSizeConditional condition={screenSize => screenSize > ScreenSize.xsmall}>
                    <HeaderCommandBarWithFilter filter={this._runsFilter} items={[]} filterToggled={this._runsFilterToggled} />
                </ScreenSizeConditional>
            );
        } else {
            return <div />;
        }
    };

    private _onClickHeaderBack = () => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const landingPageLink = linkingService.getDefinitionUrl(undefined);
        onClickFPS(this.context.pageContext, landingPageLink, true);
    };

    private _onTabChanged = (tabKey: string) => {
        this._isPageLoad = false;
        new Performance(this.context.pageContext).onFPS();
        this._hubViewState.selectedPivot.value = tabKey;
        this._setTitle(tabKey);
    };

    private _onViewStateChanged = (selectedPivot: string) => {
        this.setState({
            selectedTab: selectedPivot
        });
    };

    private _setTitle(tab: string) {
        if (this.state.pipelineDetails && !(typeof this.state.pipelineDetails === 'string')) {
            if (tab === Tabs.Branches) {
                document.title = format(Resources.TitlePipelineBranches, this.state.pipelineDetails.definitionName);
            } else if (tab === Tabs.Runs) {
                document.title = format(Resources.TitlePipelineRuns, this.state.pipelineDetails.definitionName);
            } else {
                document.title = format(Resources.Title, this.state.pipelineDetails.definitionName);
            }
        }
    }

    private _getCommandBarItems = (): IHeaderCommandBarItem[] | IObservableArray<IHeaderCommandBarItem> => {
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (!vssPageData || !tfsPageData || !tfsPageData.project) {
            return [];
        }

        if (!this._security.isMember() || this.state.pipelineDetails === undefined || typeof this.state.pipelineDetails === 'string') {
            return [];
        }

        const definitionId: number = this.state.pipelineDetails.definitionId;
        const definitionName: string = this.state.pipelineDetails.definitionName;
        const definitionPath: string = this.state.pipelineDetails.definitionPath;
        const definitionUri: string = this.state.pipelineDetails.definitionUri;
        const projectId: string = tfsPageData.project.id;
        const projectName: string = tfsPageData.project.name;
        const queueStatus: DefinitionQueueStatus = this.state.pipelineDetails.definitionQueueStatus;

        const pageContext = this.context.pageContext;
        const buildClient = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const editLink = linkingService.getDefinitionEditUrl(definitionId);
        const editYamlLink = linkingService.getYamlPipelineEditUrl(definitionId);
        const cloneLink = linkingService.getCloneDefinitionEditUrl(definitionId);
        const landingPageLink = linkingService.getDefinitionUrl(undefined);

        const isPaused: boolean = this.state.pipelineDetails.definitionQueueStatus === DefinitionQueueStatus.Paused;
        const isDisabled: boolean = this.state.pipelineDetails.definitionQueueStatus === DefinitionQueueStatus.Disabled;
        const isYamlProcess: boolean = this.state.pipelineDetails.definitionProcessType === ProcessType.Yaml;

        const canEditDefinition: boolean = this._security.canEditDefinition({ id: definitionId, path: definitionPath } as DefinitionReference);
        const canQueueBuild: boolean = this._security.canQueueBuild({ id: definitionId, path: definitionPath } as DefinitionReference);
        const canDeleteDefinition: boolean = this._security.hasDefinitionPermission(
            { id: definitionId, path: definitionPath } as DefinitionReference,
            BuildRequiredPermissions.DeleteBuildDefinition
        );

        const getExtensionContext = () => {
            return {
                id: definitionId,
                name: definitionName,
                path: definitionPath,
                project: {
                    id: projectId,
                    name: projectName
                },
                uri: definitionUri,
                type: DefinitionType.Build,
                queueStatus: queueStatus
            } as DefinitionReference;
        };

        const builtInActions: IHeaderCommandBarItem[] = [];

        builtInActions.push(PipelineActions.GetEditViewMenuItem(pageContext, canEditDefinition, isYamlProcess ? editYamlLink : editLink));

        if (canQueueBuild && !isDisabled) {
            builtInActions.push(PipelineActions.GetRunMenuItem(pageContext, projectId, definitionId));
        }

        builtInActions.push(PipelineActions.GetSecurityMenuItem(pageContext, projectId, definitionId));

        if (canEditDefinition && !isDisabled && !isYamlProcess) {
            // Yaml uses the settings panel
            builtInActions.push({
                id: "pause-resume-pipeline-command",
                text: isPaused ? Resources.ResumePipeline : Resources.PausePipeline,
                important: false,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                        retrievedDefinition.queueStatus = isPaused ? DefinitionQueueStatus.Enabled : DefinitionQueueStatus.Paused;
                        retrievedDefinition.comment = isPaused ? Resources.PipelineResumed : Resources.PipelinePaused;
                        buildClient.updateDefinition(retrievedDefinition, projectId, definitionId).then((updatedDefinition: BuildDefinition) => {
                            this._updatePipelineDetails(updatedDefinition);
                        }, handleError);
                    }, handleError);
                }
            });
        }

        if (canEditDefinition) {
            builtInActions.push(
                PipelineActions.GetRenameMoveMenuItem(pageContext, projectId, definitionId, (updatedDefinition: BuildDefinition) => {
                    if (updatedDefinition.path !== definitionPath) {
                        // If the definition is moved, permissions could change, so
                        // refresh the data and permissions from the data provider
                        // rather than just updating from the save response
                        this._dataService.refreshData();
                    } else {
                        this._updatePipelineDetails(updatedDefinition);
                    }
                })
            );
        }

        builtInActions.push({
            id: "status-badge-command",
            text: Resources.StatusBadge,
            important: false,
            onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                    this._statusBadgePanelStore.showPanel(retrievedDefinition);
                }, handleError);
            }
        });

        if (isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.EnableYamlPipelineTriggerIssues", false)) {
            builtInActions.push({
                id: Tabs.TriggerIssues,
                text: Resources.TriggerIssues,
                important: false,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    this._selectedTabIdBackUp = this.state.selectedTab;
                    this._hubViewState.selectedPivot.value = Tabs.TriggerIssues;
                    this.setState({ selectedTab: Tabs.TriggerIssues });
                }
            });
        }

        if (!isYamlProcess) {
            builtInActions.push({
                id: "designer-build-command-divider",
                important: false,
                itemType: MenuItemType.Divider
            });

            if(!this.state.isClassicPipelineCreationDisabled){
                builtInActions.push({
                    id: "clone-pipeline-command",
                    text: Resources.Clone,
                    important: false,
                    href: isYamlProcess ? undefined : cloneLink,
                    onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        onClickFPS(pageContext, cloneLink, true, event);
                    }
                });

                if (canEditDefinition) {
                    builtInActions.push({
                        id: "save-pipeline-as-template-command",
                        text: Resources.SaveAsTemplate,
                        important: false,
                        onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                            buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                                const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                                layoutManager.renderCallout(onDismiss => {
                                    const props: ISaveDefinitionProps = {
                                        pageContext: pageContext,
                                        definition: retrievedDefinition,
                                        projectId: retrievedDefinition.project.id,
                                        saveAsTemplate: true,
                                        onClose: onDismiss
                                    };
                                    return React.createElement(
                                        CommandComponent,
                                        {
                                            commandType: "ciSaveDefinitionComponent",
                                            ...props
                                        },
                                        null
                                    );
                                });
                            }, handleError);
                        }
                    });
                }
            }

            builtInActions.push({
                id: "export-pipeline-command",
                text: Resources.Export,
                important: false,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                        const definitionJson = JSON.stringify(retrievedDefinition);
                        const exportedFileName = retrievedDefinition.name.concat(".json");
                        const file = new Blob([definitionJson], { type: "application/json" });

                        if (window.navigator && (window.navigator as any).msSaveBlob) {
                            (window.navigator as any).msSaveBlob(file, exportedFileName);
                        } else {
                            const a = document.createElement("a");
                            a.href = URL.createObjectURL(file);
                            a.setAttribute("download", exportedFileName);
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        }
                    }, handleError);
                }
            });

            builtInActions.push({
                id: "export-pipelineYaml-command",
                text: Resources.ExportToYaml,
                important: false,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    buildClient.getDefinitionYaml(projectId, definitionId).then((yamlBuild: YamlBuild) => {
                        buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                            const yaml = yamlBuild.yaml;
                            const exportedFileName = retrievedDefinition.name.concat(".yml");
                            const file = new Blob([yaml], { type: "text/plain" });

                            if (window.navigator && (window.navigator as any).msSaveBlob) {
                                (window.navigator as any).msSaveBlob(file, exportedFileName);
                            } else {
                                const a = document.createElement("a");
                                a.href = URL.createObjectURL(file);
                                a.setAttribute("download", exportedFileName);
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                            }
                        }, handleError);
                    }, handleError);
                }
            });
        } else {
            builtInActions.push({
                id: "settings-pipeline-command",
                text: Resources.Settings,
                important: false,
                onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                        this._buildDefinition = retrievedDefinition;
                        this.setState({ settingsPanelIsOpen: true });
                    }, handleError);
                }
            });
        }

        if (canDeleteDefinition) {
            builtInActions.push({
                id: "delete-command-divider",
                important: false,
                itemType: MenuItemType.Divider
            });

            builtInActions.push(
                PipelineActions.GetDeleteMenuItem(pageContext, projectId, definitionId, (definition: BuildDefinition) => {
                    onClickFPS(pageContext, landingPageLink, true);
                })
            );
        }

        builtInActions.push({
            id: "status-command-divider",
            important: false,
            itemType: MenuItemType.Divider
        });

        builtInActions.push({
            id: "pipeline-scheduled-runs",
            text: Resources.PipelineScheduledRunsPanelTitle,
            important: false,
            onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                buildClient.getDefinition(projectId, definitionId).then((retrievedDefinition: BuildDefinition) => {
                    this._buildDefinition = retrievedDefinition;
                    this.setState({ scheduledRunsPanelIsOpen: true });
                }, handleError);
            }
        });

        return new ObservableHeaderCommandArray(
            this.context.pageContext,
            builtInActions,
            [
                {
                    contributionId: "ms.vss-build-web.build-definition-menu",
                    getContributionContext: getExtensionContext
                }
            ],
            ItemOrdering.StaticFirst,
            { id: "extension-actions-divider", important: false, itemType: MenuItemType.Divider }
        );
    };

    private _getCommandBarBreakpoints = (): IHeaderBreakpoint[] => {
        const { pipelineDetails } = this.state;

        if (!pipelineDetails || typeof pipelineDetails === 'string') {
            return [];
        }

        const definitionId = pipelineDetails.definitionId;
        const definitionPath = pipelineDetails.definitionPath;
        const canEditDefinition = this._security.canEditDefinition({ id: definitionId, path: definitionPath } as DefinitionReference);
        return [
            {
                breakpoint: ScreenBreakpoints.xsmall,
                commandBarItems: [
                    {
                        iconProps: undefined,
                        id: "edit-pipeline-command",
                        important: false,
                        text: canEditDefinition ? Resources.EditPipeline : Resources.ViewPipeline
                    },
                    {
                        iconProps: undefined,
                        id: "run-pipeline-command",
                        important: true,
                        text: Resources.RunPipelineShort,
                        tooltipProps: {
                            text: Resources.RunPipeline
                        }
                    }
                ]
            },
            {
                breakpoint: ScreenBreakpoints.small,
                commandBarItems: [
                    {
                        iconProps: undefined,
                        id: "edit-pipeline-command",
                        important: true,
                        text: canEditDefinition ? Resources.EditPipeline : Resources.ViewPipeline
                    },
                    {
                        iconProps: undefined,
                        id: "run-pipeline-command",
                        important: true,
                        text: Resources.RunPipeline
                    }
                ]
            }
        ];
    };

    private _getTriggerIssuesLength = (triggerIssueData: PipelineTriggerIssuesStore.ITriggerIssuesGridRow[]) => {
        let errorLength = 0;
        triggerIssueData.map(triggerIssue => {
            if (triggerIssue.statusType.color.toString() === "failed") {
                errorLength++;
            }
        });
        return errorLength;
    };

    private _dismissPipelineSettingsPanel = () => {
        this.setState({ settingsPanelIsOpen: false, settingsErrorMessage: "" });
    };

    private _dismissScheduledRunsPanel = () => {
        this.setState({ scheduledRunsPanelIsOpen: false });
    };

    private onTriggerIssuesBackButton = () => {
        let selectedTabId: string;
        if (this._selectedTabIdBackUp) {
            selectedTabId = this._selectedTabIdBackUp;
        } else {
            selectedTabId = Tabs.Runs;
        }
        this._hubViewState.selectedPivot.value = selectedTabId;

        this.setState({ selectedTab: selectedTabId });
    };

    private _onSavePipelineSettings = (changes: Partial<PipelineSettings>) => {
        if (!changes || !Object.keys(changes).length) {
            return;
        }

        const comments: string[] = [];
        const definition = { ...this._buildDefinition };
        this.setState({ settingsIsSaving: true, settingsErrorMessage: "" });
        const buildClient = this.context.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");

        // Update Queue Status
        if (changes.status !== undefined) {
            definition.queueStatus = changes.status;
            switch (changes.status) {
                case DefinitionQueueStatus.Disabled:
                    comments.push(LibraryResources.SavePipelineCommentDisable);
                    break;
                case DefinitionQueueStatus.Enabled:
                    comments.push(LibraryResources.SavePipelineCommentEnable);
                    break;
                case DefinitionQueueStatus.Paused:
                    comments.push(LibraryResources.SavePipelineCommentPause);
                    break;
                default:
                    comments.push(LibraryResources.SavePipelineCommentUnknownStatus);
                    break;
            }
        }

        // Update YAML file path
        if (changes.yamlFilePath !== undefined) {
            const yamlProcess = { ...definition.process } as YamlProcess;
            yamlProcess.yamlFilename = changes.yamlFilePath;
            definition.process = yamlProcess;
            comments.push(format(LibraryResources.SavePipelineCommentYamlFile, changes.yamlFilePath));
        }

        // Update Auto Work Item Linking Branch
        if (changes.witLinkingEnabled !== undefined || changes.witLinkingBranchFilter) {
            if (!definition.options) {
                definition.options = [];
            }
            let option = definition.options.filter(o => equals(o.definition.id, BuildOptionGUIDs.WitLinkingOptionGUID))[0];
            if (changes.witLinkingBranchFilter) {
                comments.push(format(LibraryResources.SavePipelineCommentUpdateAutoLinkBranch, changes.witLinkingBranchFilter));
                if (!option) {
                    option = {
                        definition: {
                            id: BuildOptionGUIDs.WitLinkingOptionGUID
                        },
                        inputs: {
                            branchFilters: JSON.stringify([changes.witLinkingBranchFilter])
                        },
                        enabled: true
                    };
                    definition.options.push(option);
                } else {
                    option.inputs = {
                        branchFilters: JSON.stringify([changes.witLinkingBranchFilter])
                    };
                }
            }
            if (changes.witLinkingEnabled !== undefined && option) {
                comments.push(format(LibraryResources.SavePipelineCommentEnableAutoLink, changes.witLinkingEnabled));
                option.enabled = changes.witLinkingEnabled;
            }
        }

        // Concatenate change comments for pipeline history.  Consider a dialog for a user to edit when/if needed.
        definition.comment = comments.join(". ");

        buildClient.updateDefinition(definition, definition.project.id, definition.id).then(
            (definition: BuildDefinition) => {
                this._updatePipelineDetails(definition);
                this.setState({ settingsIsSaving: false }, this._dismissPipelineSettingsPanel);
            },
            (error: Error) => {
                const message = (error && error.message) || LibraryResources.SavePipelineErrorMessage;
                this.setState({ settingsErrorMessage: message, settingsIsSaving: false });
            }
        );
    };

    private _getTabProviders = (): IReadonlyObservableArray<IVssContributedTab> => {
        return getPivotItemToTabProvider(
            this.context.pageContext,
            "ms.vss-build-web.build-definition-detail-view",
            this._getExtensionProps,
            this._viewUrls,
            this._hubViewState
        );
    };

    private _getExtensionProps = (contributionId: string): IDefinitionDetailContributionContext => {
        return {
            onDefinitionChanged: this._onExtensionDefinitionContextChanged.bind(this, contributionId)
        } as IDefinitionDetailContributionContext;
    };

    private _onExtensionDefinitionContextChanged = (contributionId: string, callback: DefinitionCallbackType) => {
        this._extensionDefinitionCallBacks[contributionId] = callback;

        const pipelineDetails = this._dataService.data.value;
        const tfsPageData = getTFSPageData(this.context.pageContext);

        if (tfsPageData && tfsPageData.project) {
            const definition: BuildDefinition = {
                id: pipelineDetails.definitionId,
                name: pipelineDetails.definitionName,
                path: pipelineDetails.definitionPath,
                uri: pipelineDetails.definitionUri,
                type: DefinitionType.Build,
                queueStatus: pipelineDetails.definitionQueueStatus,
                project: {
                    id: tfsPageData.project.id,
                    name: tfsPageData.project.name,
                    url: tfsPageData.project.url
                }
            } as BuildDefinition;

            this._extensionDefinitionCallBacks[contributionId](definition);
        }
    };

    private _updatePipelineDetails(updatedDefinition: BuildDefinition) {
        const pipelineDetails: PipelineDetailsData = {
            definitionId: updatedDefinition.id,
            definitionName: updatedDefinition.name,
            definitionPath: updatedDefinition.path,
            definitionUri: updatedDefinition.uri,
            definitionQueueStatus: updatedDefinition.queueStatus,
            definitionProcessType: updatedDefinition.process.type,
            isDraft: updatedDefinition.quality == DefinitionQuality.Draft,
            definition: updatedDefinition,
            serviceConnection: {} as PipelineServiceConnection,
            throttleMessage: "",
            incidentId: ""
        };
        this._dataService.updateData(pipelineDetails);
    }

    private _pipelineDetailsChanged = (pipelineDetails: PipelineDetailsData) => {
        const tfsPageData = getTFSPageData(this.context.pageContext);

        // Update extensions if needed
        if (tfsPageData && tfsPageData.project) {
            const definition: BuildDefinition = {
                id: pipelineDetails.definitionId,
                name: pipelineDetails.definitionName,
                path: pipelineDetails.definitionPath,
                uri: pipelineDetails.definitionUri,
                type: DefinitionType.Build,
                queueStatus: pipelineDetails.definitionQueueStatus,
                project: {
                    id: tfsPageData.project.id,
                    name: tfsPageData.project.name,
                    url: tfsPageData.project.url
                }
            } as BuildDefinition;

            const extensionKeys = Object.keys(this._extensionDefinitionCallBacks);
            extensionKeys.forEach((key: string) => {
                this._extensionDefinitionCallBacks[key](definition);
            });
        }

        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const definitionUrl = linkingService.getDefinitionUrl(pipelineDetails.definitionId);

        const breadcrumbService = this.context.pageContext.getService<PipelinesHeaderBreadcrumbService>("pipelines-header-breadcrumb-service");
        breadcrumbService.setPipeline(pipelineDetails.definitionName, definitionUrl, pipelineDetails.definitionPath);

        this.setState({
            pipelineDetails: pipelineDetails
        });
    };

    private _projectSettingsChanged = (projectSettings: GeneralSettingsData) => {
        this.setState({
            isClassicPipelineCreationDisabled: projectSettings?.disableClassicBuildPipelineCreation ? 
                projectSettings.disableClassicBuildPipelineCreation.enabled : true
        })
    }
}

Components.add("pipeline-details", PipelineDetailsViewContent);
